// src/lib/permissions.ts
export type Permission = {
  id: string;
  name?: string;
  description?: string;
  enabled: boolean;
  parentId?: string;
};

const normalizePermId = (id: string) =>
  (id ?? '')
    .toString()
    .replace(/\./g, '-')
    .toLowerCase()
    .trim();

/**
 * Load permissions from localStorage and normalize them to an array of Permission.
 * Tries several common keys and supports both array and object shapes.
 */
export function loadPermissionsFromStorage(key = 'adminPermissions'): Permission[] {
  try {
    const raw =
      (typeof window !== 'undefined' && localStorage.getItem(key)) ??
      (typeof window !== 'undefined' && localStorage.getItem('permissions')) ??
      (typeof window !== 'undefined' && localStorage.getItem('perms')) ??
      null;

    if (!raw) return [];

    const parsed = JSON.parse(raw);

    // If it's already an array
    if (Array.isArray(parsed)) {
      return parsed
        .map((p: any) => {
          if (!p) return null;
          if (typeof p === 'string') return { id: normalizePermId(p), enabled: true } as Permission;
          const id = (p.id ?? p.name ?? '').toString();
          return {
            id: normalizePermId(id),
            name: p.name,
            description: p.description,
            enabled: !!p.enabled,
            parentId: p.parentId ? normalizePermId(p.parentId) : undefined,
          } as Permission;
        })
        .filter(Boolean) as Permission[];
    }

    // If it's an object map { home: true, contact: { enabled: false } }
    if (parsed && typeof parsed === 'object') {
      return Object.keys(parsed).map((k) => {
        const v = (parsed as any)[k];
        if (v && typeof v === 'object') {
          return {
            id: normalizePermId(k),
            name: v.name,
            description: v.description,
            enabled: !!v.enabled,
            parentId: v.parentId ? normalizePermId(v.parentId) : undefined,
          } as Permission;
        }
        return { id: normalizePermId(k), enabled: !!v } as Permission;
      });
    }

    return [];
  } catch (err) {
    // safe fallback
    // eslint-disable-next-line no-console
    console.error('permissions.loadPermissionsFromStorage error:', err);
    return [];
  }
}

/**
 * Check whether a permission is enabled.
 * - `idRaw` accepts formats like "contact", "contact.location", "contact-location".
 * - If `perms` is provided, it will be used; otherwise the function reads from localStorage.
 * - By default missing permission entries -> returns `defaultAllow` (true by default).
 */
export function hasPermission(idRaw: string, perms?: Permission[] | null, defaultAllow = true): boolean {
  return true; // Master Admin removed - always allow
}

/**
 * Check both parent and sub permission are allowed.
 */
export function hasSubPermission(parentRaw: string, subRaw: string, perms?: Permission[] | null, defaultAllow = true): boolean {
  const parentOk = hasPermission(parentRaw, perms, defaultAllow);
  if (!parentOk) return false;
  return hasPermission(subRaw, perms, defaultAllow);
}
